import type { EntityConfig, EntityStatusConfig, QueryFieldConfig } from './types'
import { quoteEntity } from './definitions/quote.entity'
import { paymentEntity } from './definitions/payment.entity'
import { merchantEntity } from './definitions/merchant.entity'
import { customerEntity } from './definitions/customer.entity'
import { refundEntity } from './definitions/refund.entity'
import { disputeEntity } from './definitions/dispute.entity'
import { terminalEntity } from './definitions/terminal.entity'
import { productEntity } from './definitions/product.entity'
import { ecommerceEntity } from './definitions/ecommerce.entity'
import { paymentLinkEntity } from './definitions/payment-link.entity'
import { memberEntity } from './definitions/member.entity'
import { invoiceEntity } from './definitions/invoice.entity'
import { settlementEntity } from './definitions/settlement.entity'
import { digitalEntity } from './definitions/digital.entity'
import { eventsLogsEntity } from './definitions/eventsLogs.entity'
import { billingScheduleEntity } from './definitions/billing-schedule.entity'
import { planEntity } from './definitions/plan.entity'
import { subscriptionEntity } from './definitions/subscription.entity'
import { orderEntity } from './definitions/order.entity'
import { inventoryEntity } from './definitions/inventory.entity'
import { eventEntity } from './definitions/event.entity'
import { meterEntity } from './definitions/meter.entity'
import { creditEntity } from './definitions/credit.entity'
import { velocityEntity } from './definitions/velocity.entity'
import { applicationEntity } from './definitions/application.entity'
import { bankEntity } from './definitions/bank.entity'
import { organizationEntity } from './definitions/organization.entity'
import { cardProgramEntity } from './definitions/card-program.entity'
import { programProductEntity } from './definitions/program-product.entity'
import { mmAccountEntity } from './definitions/mm-account.entity'
import { mmReserveEntity } from './definitions/mm-reserve.entity'
import { mmOperationEntity } from './definitions/mm-operation.entity'
import { mmPayoutEntity } from './definitions/mm-payout.entity'
import { mmAdjustmentEntity } from './definitions/mm-adjustment.entity'
import { mmDepositEntity } from './definitions/mm-deposit.entity'
import { mmCollectedFeeEntity } from './definitions/mm-collected-fee.entity'
import { tariffEntity } from './definitions/tariff.entity'
import { feeEntity } from './definitions/fee.entity'
import { fxRateEntity } from './definitions/fx-rate.entity'
import { fxScheduleEntity } from './definitions/fx-schedule.entity'
import { billingReserveEntity } from './definitions/billing-reserve.entity'
import { billingPayoutEntity } from './definitions/billing-payout.entity'
import { railEntity } from './definitions/rail.entity'
import { clearingEntity } from './definitions/clearing.entity'
import { clearingBatchEntity } from './definitions/clearing-batch.entity'
import { authorizationEntity } from './definitions/authorization.entity'
import { railAlertEntity } from './definitions/rail-alert.entity'
import { reviewItemEntity } from './definitions/review-item.entity'
import { disputeRailEntity } from './definitions/dispute-rail.entity'
import { reconciliationEntity } from './definitions/reconciliation.entity'
import { networkTokenEntity } from './definitions/network-token.entity'
import { uprdrBatchEntity } from './definitions/uprdr-batch.entity'
import { nativeTokenEntity } from './definitions/native-token.entity'
import { creditGrantEntity } from './definitions/credit-grant.entity'
import { pricingTableEntity } from './definitions/pricing-table.entity'
import { revenueRecognitionPlanEntity } from './definitions/revenue-recognition-plan.entity'
import { teamMemberEntity } from './definitions/team-member.entity'
import { securityEventEntity } from './definitions/security-event.entity'
import { securityPolicyEntity } from './definitions/security-policy.entity'
import { fraudScheduleEntity } from './definitions/fraud-schedule.entity'
import { vampMonitoringEntity } from './definitions/vamp-monitoring.entity'
import { enrollmentEntity } from './definitions/enrollment.entity'
import { campaignEntity } from './definitions/campaign.entity'
import { rewardEntity } from './definitions/reward.entity'
import { pointTransactionEntity } from './definitions/point-transaction.entity'
import { tierEntity } from './definitions/tier.entity'
import { posMenuEntity } from './definitions/pos-menu.entity'
import { posInventoryEntity } from './definitions/pos-inventory.entity'
import { posRecipeEntity } from './definitions/pos-recipe.entity'
import { cardSettlementTransactionEntity } from './definitions/card-settlement-transaction.entity'

export const ENTITY_REGISTRY = {
  quote: quoteEntity,
  payment: paymentEntity,
  merchant: merchantEntity,
  customer: customerEntity,
  refund: refundEntity,
  dispute: disputeEntity,
  terminal: terminalEntity,
  product: productEntity,
  ecommerce: ecommerceEntity,
  'payment-link': paymentLinkEntity,
  member: memberEntity,
  invoice: invoiceEntity,
  settlement: settlementEntity,
  digital: digitalEntity,
  eventsLogs: eventsLogsEntity,
  'billing-schedule': billingScheduleEntity,
  plan: planEntity,
  subscription: subscriptionEntity,
  order: orderEntity,
  inventory: inventoryEntity,
  event: eventEntity,
  meter: meterEntity,
  credit: creditEntity,
  velocity: velocityEntity,
  application: applicationEntity,
  bank: bankEntity,
  organization: organizationEntity,
  'card-program': cardProgramEntity,
  'program-product': programProductEntity,
  'mm-account': mmAccountEntity,
  'mm-reserve': mmReserveEntity,
  'mm-operation': mmOperationEntity,
  'mm-payout': mmPayoutEntity,
  'mm-adjustment': mmAdjustmentEntity,
  'mm-deposit': mmDepositEntity,
  'mm-collected-fee': mmCollectedFeeEntity,
  tariff: tariffEntity,
  fee: feeEntity,
  'fx-rate': fxRateEntity,
  'fx-schedule': fxScheduleEntity,
  'billing-reserve': billingReserveEntity,
  'billing-payout': billingPayoutEntity,
  rail: railEntity,
  clearing: clearingEntity,
  'clearing-batch': clearingBatchEntity,
  authorization: authorizationEntity,
  'rail-alert': railAlertEntity,
  'review-item': reviewItemEntity,
  'dispute-rail': disputeRailEntity,
  reconciliation: reconciliationEntity,
  'network-token': networkTokenEntity,
  'uprdr-batch': uprdrBatchEntity,
  'native-token': nativeTokenEntity,
  'credit-grant': creditGrantEntity,
  'pricing-table': pricingTableEntity,
  'revenue-recognition-plan': revenueRecognitionPlanEntity,
  'team-member': teamMemberEntity,
  'security-event': securityEventEntity,
  'security-policy': securityPolicyEntity,
  'fraud-schedule': fraudScheduleEntity,
  'vamp-monitoring': vampMonitoringEntity,
  enrollment: enrollmentEntity,
  campaign: campaignEntity,
  reward: rewardEntity,
  'point-transaction': pointTransactionEntity,
  tier: tierEntity,
  'pos-menu': posMenuEntity,
  'pos-inventory': posInventoryEntity,
  'pos-recipe': posRecipeEntity,
  'card-settlement-transaction': cardSettlementTransactionEntity,
} as const

export type RegisteredEntityType = keyof typeof ENTITY_REGISTRY

export function getEntityConfig<T extends RegisteredEntityType>(entityType: T): EntityConfig {
  const config = ENTITY_REGISTRY[entityType]
  if (!config) {
    throw new Error(`Entity '${entityType}' not found in registry`)
  }
  return config
}

export function getEntityStatusConfig(entityType: RegisteredEntityType): EntityStatusConfig {
  return getEntityConfig(entityType).status
}

export function getEntityStatusFilters(entityType: RegisteredEntityType) {
  return getEntityConfig(entityType).status.filters
}

export function getEntityStatusOptions(entityType: RegisteredEntityType) {
  return getEntityConfig(entityType).status.options
}

export function getEntityStatusMappings(entityType: RegisteredEntityType) {
  return getEntityConfig(entityType).status.mappings
}

export function getEntityQueryFields(entityType: RegisteredEntityType): Record<string, QueryFieldConfig> {
  return getEntityConfig(entityType).queryFields
}

export function getEntityUIFilterFields(entityType: RegisteredEntityType): string[] {
  return getEntityConfig(entityType).uiFilterFields
}

export function isEntityRegistered(entityType: string): entityType is RegisteredEntityType {
  return entityType in ENTITY_REGISTRY
}

export type { EntityConfig, EntityStatusConfig, QueryFieldConfig } from './types'
